import jwtDecode from 'jwt-decode';
import moment from 'moment/min/moment-with-locales';
import i18next from 'i18next';
import { QuestionType } from '../Helpers/enums';

export function getUserFromToken() {
  /*global JOB_PORTAL_URL */
  const pathName = window.location.pathname;

  let jobPortalEndpoint = `${JOB_PORTAL_URL}/login`;

  if (pathName.includes('resetPassword') || pathName.includes('applicationCreation')) {
    //dont redirect in this case
    return;
  }

  const jwt = localStorage.getItem('token');
  if (jwt && jwt !== 'null' && typeof jwt === 'string' && jwt.trim() !== '') {
    try {
      let userTmp = jwtDecode(jwt);
      return userTmp;
    } catch (e) {
      // Invalid token, remove it and redirect
      localStorage.removeItem('token');
      if (window.location.href !== jobPortalEndpoint) {
        window.location.replace(jobPortalEndpoint);
      }
      return null;
    }
  } else {
    // Allow this function to be called outside react context
    if (this && this.props) {
      this.props.history.push('/login');
    } else {
      if (window.location.href !== jobPortalEndpoint) {
        window.location.replace(jobPortalEndpoint);
      }
    }
    return null;
  }
}

/* Calculate percentage of the maximum possible value for score */
export function calculatePercentage(achievedScore, maxScore) {
  return Math.ceil((achievedScore / maxScore) * 100);
}

export const calculateFullScore = (application) => {
  const questionnaireQuestions = application.answers?.filter(
    (x) => x.questionType === QuestionType.ForQuestionnaire && x.answerId !== 0
  );
  const normalQuestions = application.answers?.filter(
    (x) => x.questionType === QuestionType.Normal && x.answerId !== 0
  );

  let percentageFromNormalQuestions = 0;
  if (normalQuestions && normalQuestions.length > 0) {
    const totalScore = normalQuestions.reduce((acc, question) => acc + question.rating, 0);
    const maxScore = normalQuestions.length * 5; // Assuming each question can have a max score of 5
    percentageFromNormalQuestions = calculatePercentage(totalScore, maxScore);
  }

  let percentageFromQuestionnaireQuestions = 0;
  if (questionnaireQuestions && questionnaireQuestions.length > 0) {
    const totalScore = questionnaireQuestions.reduce((acc, question) => acc + question.rating, 0);
    const maxScore = questionnaireQuestions.length * 5; // Assuming each question can have a max score of 5
    percentageFromQuestionnaireQuestions = calculatePercentage(totalScore, maxScore);
  }

  let percentageFromCompetences = 0;
  if (application.job?.maxPossibleScore && application.score?.combined) {
    percentageFromCompetences = calculatePercentage(application.score.combined, application.job.maxPossibleScore);
  }

  let result = 0;
  const numQuestionnaireQuestions = questionnaireQuestions.length;
  const numNormalQuestions = normalQuestions.length;
  const numCompetences = application.job?.maxPossibleScore ? 1 : 0;

  if (numQuestionnaireQuestions > 0 && numNormalQuestions > 0 && numCompetences > 0) {
    result = (percentageFromNormalQuestions + percentageFromQuestionnaireQuestions + percentageFromCompetences) / 3;
  } else if (numQuestionnaireQuestions > 0 && numNormalQuestions > 0) {
    result = (percentageFromNormalQuestions + percentageFromQuestionnaireQuestions) / 2;
  } else if (numNormalQuestions > 0 && numCompetences > 0) {
    result = (percentageFromNormalQuestions + percentageFromCompetences) / 2;
  } else if (numQuestionnaireQuestions > 0 && numCompetences > 0) {
    result = (percentageFromQuestionnaireQuestions + percentageFromCompetences) / 2;
  } else if (numNormalQuestions > 0) {
    result = percentageFromNormalQuestions;
  } else if (numQuestionnaireQuestions > 0) {
    result = percentageFromQuestionnaireQuestions;
  } else if (numCompetences > 0) {
    result = percentageFromCompetences;
  } else {
    result = 0; // No questions or competences, set to 0
  }

  return Math.round(result);
};

export function getColorForPercentage(itemValue) {
  if (itemValue >= 50) {
    return 'success';
  } else if (itemValue < 50 && itemValue >= 25) {
    return 'warning';
  } else {
    return 'danger';
  }
}

export const getRadialBarClass = (percentage, size = 'xs') => {
  if (percentage === undefined || percentage === null) {
    return 'radial-bar radial-bar-' + size + ' radial-bar-danger';
  }

  const color = getColorForPercentage(percentage);

  const percentageOnChart = 'radial-bar-' + Math.round(percentage / 5) * 5;
  let fullClassName = 'radial-bar radial-bar-' + size;

  if (color === 'success') {
    fullClassName += ' radial-bar-success';
  } else if (color === 'warning') {
    fullClassName += ' radial-bar-warning';
  } else {
    fullClassName += ' radial-bar-danger';
  }

  return fullClassName + ' ' + percentageOnChart;
};

// https://getbootstrap.com/docs/4.4/components/badge/
export function getBadgeColor(itemValue) {
  if (itemValue === 'Open' || itemValue === 'Normal' || itemValue === 'Published') {
    return 'badge badge-success'; //green
  } else if (
    itemValue === 'Bounded' ||
    itemValue === 'Boolean' ||
    itemValue === 'Draft' ||
    itemValue === 'ForMostSuitableApplicant'
  ) {
    return 'badge badge-primary'; //blue
  } else if (itemValue === 'ForMostSuitableJobs') {
    return 'badge badge-info'; //light blue
  } else if (itemValue === 'Inverse' || itemValue === 'Expired') {
    return 'badge badge-danger'; // orange
  } else if (itemValue === 'Archived') {
    return 'badge badge-dark'; // black
  } else if (itemValue === 'XXX') {
    return 'badge badge-purple'; //purple
  } else return 'badge badge-warning';
}

export function getWarningSwal() {
  return {
    title: i18next.t('common.CONFIRMATION_NEEDED'),
    text: i18next.t('common.DELETE_SELECTED_ITEM'),
    icon: 'warning',
    buttons: {
      cancel: {
        text: i18next.t('common.CANCEL'),
        visible: true,
        closeModal: true,
      },
      confirm: {
        text: i18next.t('common.DELETE_CONFIRM'),
        value: true,
        visible: true,
        className: 'bg-danger',
        closeModal: true,
      },
    },
    dangerMode: true,
  };
}

//#region  Job

export function translateCompetenceType(type) {
  if (type === 'Normal') {
    return i18next.t('competences.NORMAL');
  } else if (type === 'Boolean') {
    return i18next.t('competences.BOOLEAN');
  } else if (type === 'Inverse') {
    return i18next.t('competences.INVERSE');
  }
}

export function translateApplicantType(type) {
  if (type === 'Bounded') {
    return i18next.t('applicants.BOUNDED');
  } else if (type === 'Open') {
    return i18next.t('applicants.OPEN');
  }
}

export function translateBooleanValue(value) {
  if (value) {
    if (value.toLowerCase() === 'true') {
      return i18next.t('common.TRUE');
    } else if (value.toLowerCase() === 'false') {
      return i18next.t('common.FALSE');
    } else {
      return 'Unknown';
    }
  }
}

export function getApplicationType() {
  return [
    { value: 'All', label: i18next.t('applications.SELECT_TYPE') },
    { value: 'Normal', label: i18next.t('applications.NORMAL') },
    { value: 'ForMostSuitableJobs', label: i18next.t('applications.JOB_AUTOMATIC') },
    { value: 'ForMostSuitableApplicant', label: i18next.t('applications.APPLICANT_AUTOMATIC') },
    { value: 'FromCompetences', label: i18next.t('applications.FROM_COMPETENCES') },
  ];
}

//#region Job Type
export function translateJobType(type) {
  if (type === 'Bounded') {
    return i18next.t('jobs.BOUNDED');
  } else if (type === 'Open') {
    return i18next.t('jobs.OPEN');
  }
}

export function getJobTypeList(valueName) {
  if (valueName === null || valueName === undefined) {
    return getJobType();
  } else {
    return getJobTypeLocalFull().filter((x) => x.value === valueName)[0];
  }
}

function getJobTypeLocalFull() {
  return [{ value: 'All', label: i18next.t('jobs.ALL_TYPES') }, ...getJobType()];
}

export function getJobType() {
  return [
    { value: 'Open', label: i18next.t('jobs.OPEN') },
    { value: 'Bounded', label: i18next.t('jobs.BOUNDED') },
  ];
}
//#endregion

//#region Job Experience
export function translateExperienceType(type) {
  if (type === 'EntryLevel') {
    return i18next.t('experienceType.ENTRY_LEVEL');
  } else if (type === 'MidLevel') {
    return i18next.t('experienceType.MID_LEVEL');
  } else if (type === 'Experienced') {
    return i18next.t('experienceType.EXPERIENCED');
  } else if (type === 'Senior') {
    return i18next.t('experienceType.SENIOR');
  } else if (type === 'NotApplicable') {
    return i18next.t('experienceType.NOT_APPLICABLE');
  }
}

// export function getExperienceTypeFull() {
//   return [{ value: 'All', label: i18next.t('experienceType.TYPE') }, ...getExperienceType()];
// }

export function getExperienceType() {
  return [
    { value: 'EntryLevel', label: i18next.t('experienceType.ENTRY_LEVEL') },
    { value: 'MidLevel', label: i18next.t('experienceType.MID_LEVEL') },
    { value: 'Experienced', label: i18next.t('experienceType.EXPERIENCED') },
    { value: 'Senior', label: i18next.t('experienceType.SENIOR') },
    { value: 'NotApplicable', label: i18next.t('experienceType.NOT_APPLICABLE') },
  ];
}
//#endregion

//#region Employment Type
export function getEmploymentTypeList() {
  return ['FullTime', 'PartTime', 'Seasonal', 'Student', 'Thesis', 'NotApplicable'];
}
export function translateEmploymentType(type) {
  if (type === 'Thesis') {
    return i18next.t('employmentType.THESIS');
  } else if (type === 'FullTime') {
    return i18next.t('employmentType.FULL_TIME');
  } else if (type === 'PartTime') {
    return i18next.t('employmentType.PART_TIME');
  } else if (type === 'Seasonal') {
    return i18next.t('employmentType.SEASONAL');
  } else if (type === 'Student') {
    return i18next.t('employmentType.STUDENT');
  } else if (type === 'NotApplicable') {
    return i18next.t('employmentType.NOT_APPLICABLE');
  }
}

export function getEmploymentType() {
  return [
    { value: 'FullTime', label: i18next.t('employmentType.FULL_TIME') },
    { value: 'PartTime', label: i18next.t('employmentType.PART_TIME') },
    { value: 'Seasonal', label: i18next.t('employmentType.SEASONAL') },
    { value: 'Student', label: i18next.t('employmentType.STUDENT') },
    { value: 'Thesis', label: i18next.t('employmentType.THESIS') },
    { value: 'NotApplicable', label: i18next.t('employmentType.NOT_APPLICABLE') },
  ];
}

////#endregion

export function getJobStatus() {
  return [
    { value: 'Draft', label: i18next.t('jobs.DRAFT') },
    { value: 'Published', label: i18next.t('jobs.PUBLISHED') },
    { value: 'Closed', label: i18next.t('jobs.CLOSED') },
    { value: 'Archived', label: i18next.t('jobs.ARCHIVED') },
    { value: 'Expired', label: i18next.t('jobs.EXPIRED') },
    { value: 'Deleted', label: i18next.t('jobs.DELETED') },
  ];
}
//#endregion

export function getApplicantStatus() {
  return [
    { value: 'All', label: i18next.t('applicants.SELECT_STATUS') },
    { value: 'New', label: i18next.t('applicants.NEW') },
    { value: 'InProgress', label: i18next.t('applicants.IN_PROGRESS') },
    { value: 'Hired', label: i18next.t('applicants.HIRED') },
    { value: 'OnHold', label: i18next.t('applicants.ON_HOLD') },
    { value: 'Declined', label: i18next.t('applicants.DECLINED') },
    { value: 'Deleted', label: i18next.t('applicants.DELETED') },
    { value: 'Starred', label: i18next.t('applicants.STARRED') },
  ];
}

export function getDistance() {
  return [
    { value: 25, label: '25Km' },
    { value: 50, label: '50Km' },
    { value: 75, label: '75Km' },
    { value: 100, label: '100Km' },
  ];
}

export function getApplicantTypes() {
  return [
    { value: 'All', label: i18next.t('applicants.SELECT_TYPE') },
    { value: 'Open', label: i18next.t('applicants.OPEN') },
    { value: 'Bounded', label: i18next.t('applicants.BOUNDED') },
  ];
}

export function getCompetenceType() {
  return [
    // { value: 'All', label: i18next.t('competences.SELECT_TYPE') },
    { value: 'Normal', label: i18next.t('competences.NORMAL') },
    { value: 'Boolean', label: i18next.t('competences.BOOLEAN') },
    { value: 'Inverse', label: i18next.t('competences.INVERSE') },
  ];
}

export function getJobFieldList() {
  return [
    { value: 'All Industries', label: i18next.t('jobs.ALL_INDUSTRIES') },
    { value: 'Accounting', label: i18next.t('jobs.ACCOUNTING') },
    { value: 'Administration', label: i18next.t('jobs.ADMINISTRATION') },
    { value: 'Legal', label: i18next.t('jobs.LEGAL') },
    { value: 'Defense & Aerospace', label: i18next.t('jobs.DEFENSE_AEROSPACE') },
    { value: 'Agriculture & Forestry', label: i18next.t('jobs.AGRICULTURE_FORESTRY_FISHING') },
    { value: 'Arts', label: i18next.t('jobs.ARTS') },
    { value: 'Entertainment & Recreation', label: i18next.t('jobs.ENTERTAINMENT_RECREATION') },
    { value: 'Pharmaceuticals & Biotech', label: i18next.t('jobs.PHARMACEUTICALS_BIOTECH') },
    { value: 'Business Services', label: i18next.t('jobs.BUSINESS_SERVICES') },
    { value: 'Construction, Maintenance & Repair', label: i18next.t('jobs.CONSTRUCTION_MAINTENANCE_REPAIR') },
    { value: 'Costumer Services', label: i18next.t('jobs.COSTUMER_SERVICES') },
    { value: 'Education', label: i18next.t('jobs.EDUCATION') },
    { value: 'Banking, Finance', label: i18next.t('jobs.FINANCE_BANKING') },
    { value: 'Government', label: i18next.t('jobs.GOVERNMENT') },
    { value: 'Health Care', label: i18next.t('jobs.HEALTH_CARE') },
    { value: 'IT (Information Technology)', label: i18next.t('jobs.IT') }, //15
    { value: 'Insurance', label: i18next.t('jobs.INSURANCE') },
    { value: 'Manufacturing', label: i18next.t('jobs.MANUFACTURING') },
    { value: 'Media', label: i18next.t('jobs.MEDIA') },
    { value: 'Mining & Metals', label: i18next.t('jobs.MINING_METALS') },
    { value: 'Non Profit', label: i18next.t('jobs.NON_PROFIT') },
    { value: 'Oil, Gas, Energy', label: i18next.t('jobs.OIL_GAS_ENERGY') },
    { value: 'Real Estates', label: i18next.t('jobs.REAL_ESTATES') },
    { value: 'Restaurants, Food Services & Bars', label: i18next.t('jobs.RESTAURANTS_FOODSERVICES_BARS') },
    { value: 'Retail', label: i18next.t('jobs.RETAIL') },
    { value: 'Telecommunications', label: i18next.t('jobs.TELECOMMUNICATIONS') },
    { value: 'Transportation & Logistics', label: i18next.t('jobs.TRANSPORTATION_LOGISTICS') },
    { value: 'Tourism & Travel', label: i18next.t('jobs.TOURISM_TRAVEL') },
  ];
}

export function getJobLocationType() {
  return [
    { value: 'OnSite', label: i18next.t('jobs.ON_SITE') },
    { value: 'Remote', label: i18next.t('jobs.REMOTE') },
    { value: 'PartlyRemote', label: i18next.t('jobs.PARTLY_REMOTE') },
  ];
}

export function getJobStatusList() {
  return ['Draft', 'Published', 'Closed', 'Archived', 'Expired'];
}

export function getCurrencyList() {
  return [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'GBP', label: 'GBP' }, // England pound
    { value: 'AUD', label: 'AUD' }, // Australian Dollar
    { value: 'CAD', label: 'CAD' }, // Canadian Dollar
    { value: 'CHF', label: 'CHF' }, // Swiss Franc
    { value: 'CNH', label: 'CNH' }, // Chinese Yuan
    { value: 'HKD', label: 'HKD' }, // Hong Kong Dollar
    { value: 'NZD', label: 'NZD' }, // New Zealand Dollar
  ];
}

// Check if date is in the past
export function hasExpired(date) {
  let today = moment();
  let diffFromToday = moment.utc(date) - today;

  if (diffFromToday < 0) {
    return true;
  } else {
    return false;
  }
}

export function getMineOrSelected() {
  return localStorage.getItem('MineOrAllSelected') ? localStorage.getItem('MineOrAllSelected') : '0';
}

export const hasEditingRole = (user) => {
  return user?.role === 'Admin' || user?.role === 'Owner';
};

export const hasAdminRole = (user) => {
  return user?.role === 'Admin';
};

export const averageRating = (questions) =>
  questions && questions.length > 0
    ? questions.reduce((acc, item) => acc + (item.rating || 0), 0) / questions.length
    : 0;
